import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { motionSafe, springDefault } from '@/lib/motion';
import { useLogin } from './useAuth';

export function LoginPage() {
  const login = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const totpRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  // When the server asks for a second factor, put the caret in the code box.
  // Making someone hunt for the field that just appeared is a small, avoidable
  // insult.
  useEffect(() => {
    if (mfaRequired) totpRef.current?.focus();
  }, [mfaRequired]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    login.mutate(
      {
        email,
        password,
        ...(mfaRequired && totpCode ? { totp_code: totpCode } : {}),
      },
      {
        onError: (error) => {
          if (error.mfaRequired) {
            setMfaRequired(true);
            setTotpCode('');
          }
        },
      },
    );
  };

  // The MFA prompt is a step forward, not a failure -- don't show it in red.
  const showError = login.isError && !login.error.mfaRequired;

  return (
    <main className="flex min-h-dvh items-center justify-center p-5">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={motionSafe(springDefault)}
        className="w-full max-w-sm"
      >
        <div className="mb-6">
          <h1 className="sr-only">Chronos</h1>
          <BrandLogo size="lg" decorative className="mb-3" />
          <p className="text-body text-text-secondary">Sign in to plan your day.</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
          <Field
            ref={emailRef}
            label="Email"
            type="email"
            inputMode="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={login.isPending}
          />

          <Field
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={login.isPending}
          />

          <AnimatePresence>
            {mfaRequired && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={motionSafe(springDefault)}
                // Height animation clips during the transition; without this the
                // field visibly spills past the collapsing container.
                className="overflow-hidden"
              >
                <Field
                  ref={totpRef}
                  label="Authentication code"
                  hint="Six digits from your authenticator app."
                  // numeric keyboard on phones, and one-time-code lets iOS and
                  // Android offer the code straight from the notification.
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  className="tabular"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  disabled={login.isPending}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {showError && (
            <p role="alert" className="text-small text-danger">
              {login.error.message}
            </p>
          )}

          <Button type="submit" variant="primary" loading={login.isPending}>
            {mfaRequired ? 'Verify' : 'Sign in'}
          </Button>
        </form>
      </motion.div>
    </main>
  );
}
